import { useState } from 'react'
import { App } from 'antd'
import { useQueryClient } from '@tanstack/react-query'
import { queryKeys } from '@sportninja/common/config/queryKeys'
import { mapRosterPlayerToForm } from '@sportninja/common/features/team/player/mapRosterPlayerToForm'
import { toRosterUpdatePayload } from '@sportninja/common/features/team/player/toRosterUpdatePayload'
import {
  applyInlineEdit,
  isMeaningfulEdit,
  type EditablePlayerField,
} from '@sportninja/common/features/competition/operations/utils/rosterInlineEdit'
import { useUpdateRosterPlayer } from 'src/features/team/player/hooks/useRosterPlayer'
import { useRosterContext } from 'src/pages/Team/UnifiedRoster/context/RosterContext'

interface Args {
  teamId: string
  rosterId: string
}

export interface InlinePlayerEdit {
  /** Commit one cell. Resolves once the row is back in sync with the server. */
  savePlayerField: (
    player: Record<string, unknown>,
    field: EditablePlayerField,
    value: string,
    currentValue: string
  ) => Promise<void>
  /** Ids with a save in flight, for the pending row treatment. */
  pendingIds: Set<string>
}

/**
 * Saves a single roster grid cell.
 *
 * Two things make this less trivial than the tab's other inline edits:
 *
 * 1. The PUT body is a DIFF. `toRosterUpdatePayload` compares the edited form
 *    values against the originals and sends only what changed, so editing a
 *    jersey cannot blank the player's DOB.
 * 2. `RosterContext` loads players with a raw `req()`, NOT React Query, so the
 *    mutation's own cache invalidation does not refresh this table. The
 *    context's `refresh()` has to be called explicitly or the edit would appear
 *    to do nothing until a reload.
 */
export const useInlinePlayerEdit = ({
  teamId,
  rosterId,
}: Args): InlinePlayerEdit => {
  'use memo'

  const { message } = App.useApp()
  const queryClient = useQueryClient()
  const { update } = useUpdateRosterPlayer()
  const { refresh } = useRosterContext()
  const [pendingIds, setPendingIds] = useState<Set<string>>(new Set())

  const setPending = (id: string, pending: boolean) =>
    setPendingIds((current) => {
      const next = new Set(current)
      if (pending) {
        next.add(id)
      } else {
        next.delete(id)
      }
      return next
    })

  const savePlayerField = async (
    player: Record<string, unknown>,
    field: EditablePlayerField,
    value: string,
    currentValue: string
  ) => {
    if (!isMeaningfulEdit(field, value, currentValue)) {
      return
    }

    const playerId = String(player.id ?? '')
    if (!playerId) {
      return
    }

    const initial = mapRosterPlayerToForm(player as never)
    const body = toRosterUpdatePayload(
      applyInlineEdit(initial, field, value),
      initial
    )

    setPending(playerId, true)
    try {
      await update({ teamId, rosterId, playerId, body })
      // `invalidateQueries` only REFETCHES queries that are currently mounted.
      // The single-player detail is not — the Edit modal is closed — so it is
      // merely marked stale, and React Query still serves that stale entry on
      // the modal's next open (stale-while-revalidate) before the background
      // refetch lands. The form seeds from that first render, so the user sees
      // pre-edit values. Removing the entry outright forces a real fetch.
      queryClient.removeQueries({
        queryKey: queryKeys.teamRosterPlayers.byPlayer(
          teamId,
          rosterId,
          playerId
        ),
      })
      // Raw-req context, not React Query — see the note above.
      await refresh()
    } catch (error) {
      message.error(
        error instanceof Error ? error.message : 'Could not save the change'
      )
      // Refresh on failure too, so the cell reverts to the server's value
      // instead of leaving the rejected edit on screen.
      await refresh()
    } finally {
      setPending(playerId, false)
    }
  }

  return { savePlayerField, pendingIds }
}
